import { f7, useStore } from 'framework7-react';
import { Router } from 'framework7/types';
import { useEffect, useState } from 'react';
import { customerInfoApi } from '@/api/customerApi';
import { AuthInfoState, SelectedTenant } from '@/config/initialState';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import {
  isPrepaidChargeRestrictionFlag,
  isPrepaidChargeRestricted,
  prepaidChargeRestrictionFlg,
} from '@/consts/prepaid';
import { Customer } from '@/types/api/customerApi';
import { paymentStorage } from '@/utils/paymentStorage';
import {
  getCustomerInfoRestrictionRequest,
  markPrepaidRestrictionRequestApplied,
  startPrepaidRestrictionRequest,
} from '@/utils/prepaidRestrictionRequest';
import { storeDispatch } from '@/utils/store';

type Framework7ModalRoute = typeof f7.view.main.router.currentRoute & {
  modal?: {
    once: (event: string, callback: () => void) => void;
  };
};

export type PrepaidRestrictionState = {
  customerId: number;
  selectedTenantId: number | null;
  isRestricted: boolean;
  requestId: number;
};

type PrepaidRestrictionContext = Pick<
  PrepaidRestrictionState,
  'customerId' | 'selectedTenantId'
>;

const PREPAID_CHARGE_PREVIOUS_PAGE = 'charge_prepaid';
let latestBeforeEnterRequestId = 0;

const getSelectedTenantId = (): number | null =>
  store.state.selectedTenant?.m_affiliate_id ?? null;

const isCurrentRestrictionContext = ({
  customerId,
  selectedTenantId,
}: PrepaidRestrictionContext): boolean =>
  store.state.authInfo?.m_customer_id === customerId &&
  getSelectedTenantId() === selectedTenantId;

const startBeforeEnterRequest = (): number => ++latestBeforeEnterRequestId;

const isLatestBeforeEnterRequest = (
  requestId: number,
  restrictionContext: PrepaidRestrictionContext,
): boolean =>
  requestId === latestBeforeEnterRequestId &&
  isCurrentRestrictionContext(restrictionContext);

export const fetchPrepaidRestrictionState = async (
  customerId: number,
  selectedTenantId: number | null,
  fallbackRequestId: number,
): Promise<PrepaidRestrictionState> => {
  const { data: customerInfo } = await customerInfoApi({
    m_customer_id: customerId,
  });
  if (!customerInfo.success || !customerInfo.customer) {
    throw new Error('プリペイド制限状態を取得できませんでした');
  }
  const restrictionFlag = customerInfo.customer.prepaid_charge_restriction_flg;
  if (!isPrepaidChargeRestrictionFlag(restrictionFlag)) {
    throw new Error('プリペイド制限状態を取得できませんでした');
  }
  const requestContext = getCustomerInfoRestrictionRequest(
    customerInfo.customer,
  ) ?? {
    customerId,
    selectedTenantId,
    requestId: fallbackRequestId,
  };
  if (
    !requestContext ||
    requestContext.customerId !== customerId ||
    requestContext.selectedTenantId !== selectedTenantId
  ) {
    throw new Error('プリペイド制限状態を取得できませんでした');
  }

  return {
    customerId,
    selectedTenantId,
    isRestricted: restrictionFlag === prepaidChargeRestrictionFlg.RESTRICTED,
    requestId: requestContext.requestId,
  };
};

export const applyPrepaidRestrictionState = async (
  restrictionState: PrepaidRestrictionState,
): Promise<boolean> => {
  if (!isCurrentRestrictionContext(restrictionState)) return false;
  if (!markPrepaidRestrictionRequestApplied(restrictionState.requestId)) {
    return false;
  }

  await storeDispatch('setCustomerInfo', 'customerInfo', {
    prepaid_charge_restriction_flg: restrictionState.isRestricted
      ? prepaidChargeRestrictionFlg.RESTRICTED
      : prepaidChargeRestrictionFlg.NONE,
  });
  if (!isCurrentRestrictionContext(restrictionState)) return false;
  await storeDispatch(
    'setPrepaidRestrictionCustomerId',
    'prepaidRestrictionCustomerId',
    restrictionState.customerId,
  );
  return isCurrentRestrictionContext(restrictionState);
};

const applyUnavailablePrepaidRestrictionState = async (
  customerId: number,
  selectedTenantId: number | null,
  requestId: number,
): Promise<boolean> => {
  try {
    return await applyPrepaidRestrictionState({
      customerId,
      selectedTenantId,
      isRestricted: true,
      requestId,
    });
  } catch {
    return false;
  }
};

export const refreshPrepaidRestrictionAfterRejectedOperation = async (
  customerId: number,
): Promise<boolean> => {
  const selectedTenantId = getSelectedTenantId();
  const fallbackRequestId = startPrepaidRestrictionRequest();
  try {
    const restrictionState = await fetchPrepaidRestrictionState(
      customerId,
      selectedTenantId,
      fallbackRequestId,
    );
    return await applyPrepaidRestrictionState(restrictionState);
  } catch {
    return await applyUnavailablePrepaidRestrictionState(
      customerId,
      selectedTenantId,
      startPrepaidRestrictionRequest(),
    );
  }
};

const clearRestrictedPrepaidNavigationState = (routePath?: string): void => {
  if (routePath === paths.prepaidSummary || routePath === paths.reservings) {
    if (paymentStorage.getItem('destination_page') === paths.reservings) {
      paymentStorage.removeItem('destination_step');
      paymentStorage.removeItem('destination_page');
    }
    paymentStorage.removeItem('reserve_amount');
    paymentStorage.removeItem('prepaid_registration_info');
    window.localStorage.removeItem('termListCheckbox');
    paymentStorage.removeItem('paymentRedirectPage');
    window.localStorage.removeItem('redirectCarWashAfterPayment');
    if (
      routePath === paths.reservings &&
      paymentStorage.getItem('source_page') === paths.chargePrepaid
    ) {
      paymentStorage.removeItem('source_page');
    }
    storeDispatch('setReserveAmount', 'reserveAmount', 0);
    return;
  }

  if (routePath !== paths.chargePrepaid) return;

  if (paymentStorage.getItem('source_page') === paths.chargePrepaid) {
    paymentStorage.removeItem('source_page');
  }
  if (paymentStorage.getItem('destination_page') === paths.chargePrepaid) {
    paymentStorage.removeItem('destination_step');
    paymentStorage.removeItem('destination_page');
  }
  paymentStorage.removeItem('charge_amount');
  paymentStorage.removeItem('postGmoNav');
  if (paymentStorage.getItem('prevPage') === PREPAID_CHARGE_PREVIOUS_PAGE) {
    paymentStorage.removeItem('prevPage');
  }
};

export const redirectFromRestrictedPrepaidRoute = (routePath?: string) => {
  clearRestrictedPrepaidNavigationState(routePath);
  storeDispatch('setMenu', 'menu', 'prepaid');
  f7.view.main.router.navigate(paths.prepaid, {
    reloadAll: true,
    clearPreviousHistory: true,
  });
};

export const prepaidRestrictionBeforeEnter = ({
  to,
  resolve,
  reject,
}: Router.RouteCallbackCtx): void => {
  const customerId = store.state.authInfo?.m_customer_id;
  const selectedTenantId = getSelectedTenantId();
  const requestId = startBeforeEnterRequest();
  const fallbackRequestId = startPrepaidRestrictionRequest();
  if (!customerId) {
    reject();
    redirectFromRestrictedPrepaidRoute(to?.path);
    return;
  }

  fetchPrepaidRestrictionState(customerId, selectedTenantId, fallbackRequestId)
    .then(async (restrictionState) => {
      if (!isLatestBeforeEnterRequest(requestId, restrictionState)) {
        reject();
        return;
      }
      const wasApplied = await applyPrepaidRestrictionState(restrictionState);
      if (
        !wasApplied ||
        !isLatestBeforeEnterRequest(requestId, restrictionState)
      ) {
        reject();
        return;
      }
      if (!restrictionState.isRestricted) {
        resolve();
        return;
      }
      reject();
      redirectFromRestrictedPrepaidRoute(to?.path);
    })
    .catch(async () => {
      reject();
      const restrictionContext = { customerId, selectedTenantId };
      if (isLatestBeforeEnterRequest(requestId, restrictionContext)) {
        await applyUnavailablePrepaidRestrictionState(
          customerId,
          selectedTenantId,
          fallbackRequestId,
        );
      }
      if (isLatestBeforeEnterRequest(requestId, restrictionContext)) {
        redirectFromRestrictedPrepaidRoute(to?.path);
      }
    });
};

const redirectFromRestrictedPrepaidPopupRoute = () => {
  const router = f7.view.main.router;
  clearRestrictedPrepaidNavigationState(router.currentRoute.path);
  const modal = (router.currentRoute as Framework7ModalRoute).modal;
  if (!modal) return;

  storeDispatch('setMenu', 'menu', 'prepaid');
  modal.once('closed', () => {
    if (router.currentRoute.path === paths.prepaid) {
      router.clearPreviousHistory();
      return;
    }
    router.navigate(paths.prepaid, {
      reloadAll: true,
      clearPreviousHistory: true,
    });
  });
  router.back(undefined, { animate: false });
};

/**
 * 制限フラグの表示判定（押せない / 確認中）。
 * refreshOnMount のとき、同じ顧客について基礎情報APIから反映済みの制限フラグ
 * （prepaidRestrictionCustomerId === customerId）があればそれで即表示し、確認中にしない。
 * 制限フラグは加盟法人のマスタ設定のため。表示中に変わっても遷移前ガードとサーバーで止まる。
 * 反映済みの値が無い（初期値のまま等）ときは、この画面での取得が済むまで確認中にする。
 */
export const resolvePrepaidRestrictionView = ({
  enabled,
  refreshOnMount,
  customerId,
  prepaidRestrictionCustomerId,
  appliedRestrictionFlag,
  fetchedCustomerId,
  isChecking,
  restrictionStateUnavailable,
}: {
  enabled: boolean;
  refreshOnMount: boolean;
  customerId: number | undefined;
  prepaidRestrictionCustomerId: number | null;
  appliedRestrictionFlag: unknown;
  fetchedCustomerId: number | undefined;
  isChecking: boolean;
  restrictionStateUnavailable: boolean;
}): { isRestricted: boolean; shouldKeepChecking: boolean } => {
  const hasCurrentRestrictionState =
    enabled && !!customerId && fetchedCustomerId === customerId;
  const hasAppliedRestrictionState = isPrepaidChargeRestrictionFlag(
    appliedRestrictionFlag,
  );
  const hasStoredRestrictionState =
    enabled &&
    !!customerId &&
    prepaidRestrictionCustomerId === customerId &&
    hasAppliedRestrictionState;
  const isRestricted = enabled
    ? !customerId ||
      restrictionStateUnavailable ||
      (refreshOnMount
        ? (hasCurrentRestrictionState || hasStoredRestrictionState) &&
          isPrepaidChargeRestricted(appliedRestrictionFlag)
        : !hasAppliedRestrictionState ||
          isPrepaidChargeRestricted(appliedRestrictionFlag))
    : false;
  const shouldKeepChecking =
    enabled &&
    refreshOnMount &&
    !hasStoredRestrictionState &&
    (isChecking || (!!customerId && !hasCurrentRestrictionState));
  return { isRestricted, shouldKeepChecking };
};

export const usePrepaidRestrictionGuard = ({
  isPopup = false,
  enabled = true,
  redirectWhenRestricted = true,
  refreshOnMount = true,
}: {
  isPopup?: boolean;
  enabled?: boolean;
  redirectWhenRestricted?: boolean;
  refreshOnMount?: boolean;
} = {}) => {
  const authInfo = useStore(store, 'authInfo') as AuthInfoState;
  const selectedTenant = useStore(store, 'getSelectedTenant') as SelectedTenant;
  const customerInfo = useStore(store, 'customerInfo') as Customer;
  const prepaidRestrictionCustomerId = useStore(
    store,
    'prepaidRestrictionCustomerId',
  ) as number | null;
  const [isChecking, setIsChecking] = useState(enabled && refreshOnMount);
  const [restrictionStateUnavailable, setRestrictionStateUnavailable] =
    useState(false);
  const [restrictionState, setRestrictionState] =
    useState<PrepaidRestrictionState>();
  const customerId = authInfo.m_customer_id;
  const selectedTenantId = selectedTenant?.m_affiliate_id ?? null;
  // この画面での取得結果は、今選んでいる法人のものだけ使う
  // （取得済みの制限フラグの印は、法人の切替時に store 側で外す）
  const { isRestricted, shouldKeepChecking } = resolvePrepaidRestrictionView({
    enabled,
    refreshOnMount,
    customerId,
    prepaidRestrictionCustomerId,
    appliedRestrictionFlag: customerInfo.prepaid_charge_restriction_flg,
    fetchedCustomerId:
      restrictionState?.selectedTenantId === selectedTenantId
        ? restrictionState?.customerId
        : undefined,
    isChecking,
    restrictionStateUnavailable,
  });

  useEffect(() => {
    if (!enabled || !refreshOnMount) {
      setIsChecking(false);
      setRestrictionStateUnavailable(false);
      setRestrictionState(undefined);
      return;
    }
    if (!customerId) {
      setIsChecking(false);
      setRestrictionStateUnavailable(true);
      return;
    }

    let active = true;
    const fallbackRequestId = startPrepaidRestrictionRequest();
    setIsChecking(true);
    setRestrictionStateUnavailable(false);
    fetchPrepaidRestrictionState(
      customerId,
      selectedTenantId,
      fallbackRequestId,
    )
      .then(async (nextRestrictionState) => {
        if (!active || !isCurrentRestrictionContext(nextRestrictionState)) {
          return;
        }
        const wasApplied = await applyPrepaidRestrictionState(
          nextRestrictionState,
        );
        if (!active) return;
        if (!wasApplied) {
          if (isCurrentRestrictionContext(nextRestrictionState)) {
            setRestrictionState(nextRestrictionState);
          }
          return;
        }
        setRestrictionState(nextRestrictionState);
      })
      .catch(async () => {
        if (
          active &&
          isCurrentRestrictionContext({ customerId, selectedTenantId })
        ) {
          await applyUnavailablePrepaidRestrictionState(
            customerId,
            selectedTenantId,
            fallbackRequestId,
          );
        }
        if (
          active &&
          isCurrentRestrictionContext({ customerId, selectedTenantId })
        ) {
          setRestrictionStateUnavailable(true);
        }
      })
      .finally(() => {
        if (active) setIsChecking(false);
      });
    return () => {
      active = false;
    };
  }, [customerId, enabled, refreshOnMount, selectedTenantId]);

  useEffect(() => {
    if (!redirectWhenRestricted || shouldKeepChecking || !isRestricted) {
      return;
    }

    let animationFrame = 0;
    const redirectWhenRouteIsReady = () => {
      const currentRoute = f7.view.main.router
        .currentRoute as Framework7ModalRoute;
      if (isPopup && !currentRoute.modal) {
        animationFrame = window.requestAnimationFrame(redirectWhenRouteIsReady);
        return;
      }
      if (isPopup) {
        redirectFromRestrictedPrepaidPopupRoute();
      } else {
        redirectFromRestrictedPrepaidRoute(
          f7.view.main.router.currentRoute.path,
        );
      }
    };
    animationFrame = window.requestAnimationFrame(redirectWhenRouteIsReady);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [isPopup, isRestricted, redirectWhenRestricted, shouldKeepChecking]);

  return {
    isChecking: shouldKeepChecking,
    isRestricted,
  };
};
